/* ==========================================================================
   PRESCH — globales Custom-JS
   Eingebunden über snippets/custom.globals.liquid (defer).

   Regeln:
   - Horizon nutzt Web Components mit einem ref/on:-System
     (siehe assets/component.js). Vendor-Komponenten NICHT monkey-patchen —
     stattdessen eigene Custom Elements registrieren, Namensraum `custom-`.
   - Keine Preis- oder Mengenlogik hier duplizieren: die Quelle der Wahrheit
     sind snippets/custom.price.liquid und custom.quantity-input.liquid.
   - Keine Texte hier hartkodieren (Steuer-/Preishinweise): aus dem Markup
     bzw. per data-Attribut aus den Locales übergeben.
   ========================================================================== */

(() => {
  'use strict';

  // B2B-Layer (später): Nettopreis-Umschaltung, Staffelpreis-Interaktion,
  // Mengenregel-Validierung. Einhängepunkte hier ergänzen.
})();

/* ==========================================================================
   PRESCH — Suche: Artikelnummer-Auflösung
   ==========================================================================
   Warum das existiert (gemessen am 2026-08-07 gegen die Live-Domain):
   Shopifys Suche verzeiht einfache Tippfehler bereits ("wasserwage" -> Treffer)
   und wendet die im Shop gepflegten Synonyme an. Sie findet aber KEINE
   Artikelnummern: "10426" liefert 0 Treffer, obwohl es eine echte SKU ist.
   Handwerker und Firmenkunden (16-20 % Umsatzanteil) suchen genau danach.

   Deshalb greift dieser Layer bewusst NUR bei Artikelnummern ein. Alles andere
   bleibt bei Shopify, damit die manuell gepflegten Synonyme nicht ausgehebelt
   werden und kein zweiter Suchindex gepflegt werden muss.

   Datenquelle: /search?view=custom-sku (templates/search.custom-sku.liquid)
   ========================================================================== */
(() => {
  'use strict';

  const CACHE_KEY = 'presch.skuIndex.v1';
  const SKU_PATTERN = /^[0-9]{3,8}$/;
  let indexPromise = null;

  /** Lädt das SKU-Verzeichnis einmal pro Sitzung. */
  function loadIndex() {
    if (indexPromise) return indexPromise;

    indexPromise = (async () => {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached);
      } catch (_) { /* sessionStorage kann blockiert sein */ }

      const map = {};
      let page = 1;
      let hasNext = true;

      while (hasNext && page <= 10) {
        const response = await fetch(`/search?view=custom-sku&page=${page}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) break;
        const data = await response.json();
        for (const item of data.items || []) {
          for (const sku of item.s || []) {
            map[String(sku).toLowerCase()] = { h: item.h, t: item.t, sku: String(sku) };
          }
        }
        hasNext = Boolean(data.next);
        page += 1;
      }

      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(map)); } catch (_) {}
      return map;
    })();

    return indexPromise;
  }

  const looksLikeSku = (value) => SKU_PATTERN.test(String(value).trim());

  async function resolveSku(value) {
    const map = await loadIndex();
    return map[String(value).trim().toLowerCase()] || null;
  }

  /** Blendet den Treffer über den regulären Suchergebnissen ein. */
  function renderHit(form, hit) {
    let box = form.querySelector('.custom-sku-hit');

    if (!hit) {
      if (box) box.remove();
      return;
    }
    if (!box) {
      box = document.createElement('a');
      box.className = 'custom-sku-hit';
      form.appendChild(box);
    }
    box.href = `/products/${hit.h}`;
    box.innerHTML =
      `<span class="custom-sku-hit__label">Art.-Nr. ${hit.sku}</span>` +
      `<span class="custom-sku-hit__title"></span>`;
    box.querySelector('.custom-sku-hit__title').textContent = hit.t;
  }

  function bind(input) {
    if (input.dataset.customSkuBound) return;
    input.dataset.customSkuBound = '1';

    const form = input.closest('form') || input.parentElement;
    let timer = null;

    // Verzeichnis vorwärmen, sobald das Feld benutzt wird. Damit ist der erste
    // echte Treffer sofort da statt erst nach dem Nachladen.
    input.addEventListener('focus', () => { loadIndex().catch(() => {}); }, { once: true });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const value = input.value;

      if (!looksLikeSku(value)) {
        renderHit(form, null);
        return;
      }
      // Nur bei Artikelnummern überhaupt nachschlagen.
      timer = setTimeout(async () => {
        // Der erste Aufruf lädt das Verzeichnis und dauert deshalb länger als
        // die Tipp-Geschwindigkeit. Ohne diesen Abgleich rendert ein spätes
        // Ergebnis auf einer Eingabe, zu der es nicht mehr gehört (belegt im
        // Test: Wortsuche zeigte den Treffer der vorherigen Artikelnummer).
        const pending = value;
        try {
          const hit = await resolveSku(pending);
          if (input.value !== pending) return;
          renderHit(form, hit);
        } catch (_) {
          if (input.value === pending) renderHit(form, null);
        }
      }, 150);
    });
  }

  function scan() {
    document.querySelectorAll('input[name="q"]').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', scan);
  // Horizon öffnet die Suche als Drawer, das Feld existiert dann evtl. noch nicht.
  document.addEventListener('click', () => setTimeout(scan, 120));
  scan();
})();

/* ==========================================================================
   PRESCH — Suche: Tippfehler-Korrektur ("Meintest du …?")
   ==========================================================================
   Greift bewusst NUR, wenn Shopify null Produkte liefert. Damit bleiben die
   im Shop gepflegten Synonyme und Shopifys eigene Fehlertoleranz unangetastet
   (gemessen: "wasserwage" und "zwingr" findet Shopify bereits selbst).
   Ergänzt werden nur die belegten Lücken: vertauschte Buchstaben
   ("wasserwaqe") und Mehrfachfehler ("forsnerborer").

   Das Wortverzeichnis entsteht aus den Produkttiteln, die der SKU-Index
   ohnehin lädt — kein zweiter Endpunkt, kein zweiter Index zum Pflegen.
   ========================================================================== */
(() => {
  'use strict';

  const MIN_LENGTH = 4;
  let vocabPromise = null;

  /** Wortverzeichnis aus den Produkttiteln des SKU-Index. */
  function loadVocabulary() {
    if (vocabPromise) return vocabPromise;

    vocabPromise = (async () => {
      const words = new Map(); // wort -> Häufigkeit
      let page = 1;
      let hasNext = true;

      while (hasNext && page <= 10) {
        const response = await fetch(`/search?view=custom-sku&page=${page}`);
        if (!response.ok) break;
        const data = await response.json();
        for (const item of data.items || []) {
          const tokens = String(item.t || '')
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, ' ')
            .split(' ');
          for (const token of tokens) {
            if (token.length >= MIN_LENGTH && !/^\d+$/.test(token)) {
              words.set(token, (words.get(token) || 0) + 1);
            }
          }
        }
        hasNext = Boolean(data.next);
        page += 1;
      }
      return [...words.entries()].map(([word, count]) => ({ word, count }));
    })();

    return vocabPromise;
  }

  /** Levenshtein-Distanz mit Abbruch, sobald das Limit überschritten ist. */
  function distance(a, b, limit) {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);

    for (let i = 1; i <= a.length; i += 1) {
      const row = [i];
      let best = i;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
        if (row[j] < best) best = row[j];
      }
      if (best > limit) return limit + 1;
      prev = row;
    }
    return prev[b.length];
  }

  /** Erlaubt mehr Abweichung bei längeren Wörtern. */
  const toleranceFor = (word) => (word.length <= 5 ? 1 : word.length <= 9 ? 2 : 3);

  async function correct(term) {
    const query = term.toLowerCase().trim();
    if (query.length < MIN_LENGTH) return null;

    const vocabulary = await loadVocabulary();
    const limit = toleranceFor(query);
    let best = null;

    for (const entry of vocabulary) {
      const d = distance(query, entry.word, limit);
      if (d === 0) return null; // Wort existiert -> nichts zu korrigieren
      if (d <= limit && (!best || d < best.d || (d === best.d && entry.count > best.count))) {
        best = { word: entry.word, d, count: entry.count };
      }
    }
    return best ? best.word : null;
  }

  async function shopifyHasResults(term) {
    const url = `/search/suggest.json?q=${encodeURIComponent(term)}` +
      '&resources[type]=product&resources[limit]=4';
    const response = await fetch(url);
    if (!response.ok) return true; // im Zweifel nicht eingreifen
    const data = await response.json();
    return (data.resources?.results?.products || []).length > 0;
  }

  function renderSuggestion(form, input, suggestion) {
    let box = form.querySelector('.custom-did-you-mean');

    if (!suggestion) {
      if (box) box.remove();
      return;
    }
    if (!box) {
      box = document.createElement('button');
      box.type = 'button';
      box.className = 'custom-did-you-mean';
      box.addEventListener('click', () => {
        input.value = box.dataset.term;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
      form.appendChild(box);
    }
    box.dataset.term = suggestion;
    box.textContent = '';
    box.append('Meintest du ');
    const strong = document.createElement('strong');
    strong.textContent = suggestion;
    box.append(strong, '?');
  }

  function bind(input) {
    if (input.dataset.customFuzzyBound) return;
    input.dataset.customFuzzyBound = '1';

    const form = input.closest('form') || input.parentElement;
    let timer = null;

    // Wortverzeichnis vorwärmen. Ohne das war der ERSTE Tippfehler ohne
    // Vorschlag: das Laden dauerte länger als die Eingabe, und der
    // Aktualitäts-Abgleich verwarf das Ergebnis korrekt, aber wirkungslos.
    input.addEventListener('focus', () => { loadVocabulary().catch(() => {}); }, { once: true });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const value = input.value.trim();

      if (value.length < MIN_LENGTH || /^\d+$/.test(value)) {
        renderSuggestion(form, input, null);
        return;
      }

      timer = setTimeout(async () => {
        const pending = value;
        try {
          if (await shopifyHasResults(pending)) {
            if (input.value.trim() === pending) renderSuggestion(form, input, null);
            return;
          }
          const suggestion = await correct(pending);
          // Gleicher Abgleich wie beim SKU-Layer: späte Antworten dürfen nicht
          // auf einer inzwischen geänderten Eingabe rendern.
          if (input.value.trim() !== pending) return;
          renderSuggestion(form, input, suggestion);
        } catch (_) {
          if (input.value.trim() === pending) renderSuggestion(form, input, null);
        }
      }, 350);
    });
  }

  const scan = () => document.querySelectorAll('input[name="q"]').forEach(bind);

  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('click', () => setTimeout(scan, 120));
  scan();
})();

/* Anker-Nachjustierung (Team-Meldung 2026-08-27: „Technische Daten" landete
   beim Kollegen zu tief, Richtung Footer — hier nicht reproduzierbar).

   Plausibelste Ursache: Der Sprung laeuft als Smooth-Animation auf eine beim
   Klick berechnete Koordinate. Laden waehrenddessen noch Inhalte OBERHALB des
   Ziels nach (Bilder, Schrift-Reflow), verschiebt sich das Ziel und die
   Animation landet daneben. Deshalb: Nach jedem internen Ankerklick einmal
   nachmessen und still korrigieren — aber nur, wenn der Nutzer nicht selbst
   weitergescrollt hat und die Abweichung nach Verschiebung aussieht
   (40–800px), nicht nach Absicht. */
document.addEventListener('click', (event) => {
  const anker = event.target instanceof Element && event.target.closest('a[href^="#"]');
  if (!anker) return;
  const ziel = document.getElementById(anker.getAttribute('href').slice(1));
  if (!ziel) return;

  let selbstGescrollt = false;
  const merken = () => { selbstGescrollt = true; };
  addEventListener('wheel', merken, { once: true, passive: true });
  addEventListener('touchmove', merken, { once: true, passive: true });

  setTimeout(() => {
    removeEventListener('wheel', merken);
    removeEventListener('touchmove', merken);
    if (selbstGescrollt) return;
    const soll = parseFloat(getComputedStyle(ziel).scrollMarginTop) || 0;
    const abweichung = Math.abs(ziel.getBoundingClientRect().top - soll);
    if (abweichung > 40 && abweichung < 800) {
      ziel.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, 700);
});

/* Passwortmanager-Schutz (Aufloesung der „Technische Daten"-Meldung vom
   27.08.: Der Melder hat es selbst eingegrenzt — mit angemeldetem LastPass
   springt die Seite zum Footer, ohne nicht. LastPass fokussiert beim
   Feld-Scan das einzige E-Mail-Feld der Seite, das Newsletter-Feld im
   Footer, und der Browser scrollt zum Fokus.)

   `data-lpignore` ist LastPass' dokumentiertes Opt-out je Feld; das
   Suchfeld nehmen wir mit, damit dort kein Ausfuell-Icon erscheint.
   Nachtraeglich gesetzt (die Felder sind Vendor-Markup) — LastPass scannt
   bei DOM-Aenderungen erneut und liest das Attribut dann mit. */
const passwortmanagerFernhalten = () => {
  document
    .querySelectorAll('.email-signup__input, input[type="search"]')
    .forEach((feld) => feld.setAttribute('data-lpignore', 'true'));
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', passwortmanagerFernhalten, { once: true });
} else {
  passwortmanagerFernhalten();
}

/* Klaviyo-Back-in-Stock-Knopf uebersetzen (Fund 2026-08-28: auf FR/NL-Seiten
   stand deutsch „Benachrichtige mich"). Der Knopf wird von Klaviyos
   Onsite-Embed injiziert und traegt den einsprachig im Klaviyo-Konto
   gepflegten Text; eigene Ausloeser (klaviyo-bis-trigger) versteckt das
   Embed aktiv, statt sie zu uebernehmen. Also tauschen wir die Beschriftung
   des injizierten Knopfs. Der Zieltext kommt aus den Theme-Sprachdateien
   (custom.notify_me) via data-bis-label an der PDP-Hero-Section.

   Beobachter statt Einmal-Lauf, weil Klaviyo den Knopf asynchron rendert
   und bei eigenen Re-Renders den Text zuruecksetzen kann; die Schleife
   endet, weil nur bei abweichendem Text geschrieben wird. Erkennung ueber
   Klaviyos stabiles data-a11y-identifier="bis-button-…". */
const klaviyoBisUebersetzen = () => {
  const traeger = document.querySelector('[data-bis-label]');
  if (!traeger) return;
  const label = traeger.dataset.bisLabel;
  if (!label) return;

  /* Das Fenster dahinter (07.10.): Klaviyo kennt nur EIN Back-in-Stock-Formular
     je Konto, deutsch. Die Texte unten sind die im Klaviyo-Konto gepflegten
     (Formular WVTejA „Back in Stock Multi-step"); ändert jemand sie dort, hier
     nachziehen, sonst bleibt der geänderte Text deutsch. Übersetzungen aus
     custom.bis_formular (script[data-bis-formular], nur außerhalb DE). */
  const quelle = {
    titel: 'Benachrichtige mich',
    text: 'Bekomme eine Nachricht, sobald der Artikel wieder auf Lager ist.',
    email: 'Deine E-Mail',
    newsletter: 'Neuigkeiten und Angebote via E-Mail erhalten.',
    knopf: 'Informiere mich!',
    fehler_email: 'keine gültige Email-Adresse',
    pflichtfeld: 'Pflichtfeld', // Klaviyo-Standardmeldung bei leerem Feld
    erfolg_titel: 'Du stehst auf der Liste!',
    erfolg_text: 'Wir melden uns, sobald das Produkt wieder da ist.',
  };
  let tausch = null;
  try {
    const ziel = JSON.parse(document.querySelector('script[data-bis-formular]')?.textContent || 'null');
    if (ziel) {
      tausch = new Map();
      Object.keys(quelle).forEach((k) => ziel[k] && tausch.set(quelle[k], ziel[k]));
    }
  } catch (e) {}

  const fensterUebersetzen = () => {
    document.querySelectorAll('.klaviyo-form').forEach((formular) => {
      const w = document.createTreeWalker(formular, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = w.nextNode())) {
        const neu = tausch.get(n.nodeValue.trim());
        if (neu && n.nodeValue !== neu) n.nodeValue = neu;
      }
      formular.querySelectorAll('[placeholder], [aria-label]').forEach((el) => {
        ['placeholder', 'aria-label'].forEach((a) => {
          const neu = el.hasAttribute(a) && tausch.get(el.getAttribute(a).trim());
          if (neu && el.getAttribute(a) !== neu) el.setAttribute(a, neu);
        });
      });
    });
  };

  const anwenden = () => {
    document
      .querySelectorAll('[data-a11y-identifier^="bis-button"] button, button[data-a11y-identifier^="bis-button"]')
      .forEach((knopf) => {
        if (knopf.textContent !== label) knopf.textContent = label;
      });
    if (tausch) fensterUebersetzen();
  };

  anwenden();
  new MutationObserver(anwenden).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: Boolean(tausch),
  });
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', klaviyoBisUebersetzen, { once: true });
} else {
  klaviyoBisUebersetzen();
}

/* ==========================================================================
   PRESCH — A/B-Test-Messung: Bestellungen der Horizon-Variante markieren
   ==========================================================================
   Das Rollouts-Dashboard zeigt je Variante nur Conversion-, Bounce-,
   Add-to-Cart- und Reached-Checkout-Rate — KEINEN Umsatz und KEINEN AOV.
   Deshalb schreibt dieser Baustein das Cart-Attribut `_theme_variant` in
   jeden Warenkorb, der in dieser Theme-Variante entsteht. Das Attribut landet
   automatisch an der Bestellung (Admin > Bestellung > Zusaetzliche Details)
   und macht Umsatz/AOV je Variante nachtraeglich auswertbar.
   Der Unterstrich haelt das Attribut aus kundensichtbaren Anzeigen heraus.

   Wert seit 2026-10-05: `horizon-<Theme-ID>` (ID aus window.preschTheme,
   gesetzt in snippets/custom.globals.liquid). Vorher stand fest `horizon`,
   was beim Mengenrabatt-Test (zwei Horizon-Themes: Live #185766183177 = A,
   Test #186051330313 = B) nichts unterschied. Bestellungen vor dem 05.10.
   lassen sich darum nicht nach Variante trennen.

   Ausloeser: Horizons Standard-Event `shopify:cart:lines-update` (feuert
   bei jedem Hinzufuegen/Aendern, siehe assets/standard-events.d.ts) plus
   ein Lauf beim Seitenstart, falls schon ein Warenkorb existiert (erkennbar
   am `cart`-Cookie — erst dann lohnt der /cart.js-Abruf). Kein Patch an
   window.fetch und kein Eingriff in Vendor-Komponenten. Unser eigener
   /cart/update.js-Aufruf loest das DOM-Event nicht aus — keine Schleife.

   NACH DEM TEST ENTFERNEN (Block + Doku-Eintrag), Auswertung nur fuer
   Bestellungen ab Einbau-Datum und nur Kanal Online Store.
   ========================================================================== */
(() => {
  'use strict';

  const ATTRIBUT = '_theme_variant';
  const themeId = window.preschTheme && window.preschTheme.id;
  const WERT = 'horizon-' + (themeId || 'unbekannt');
  let laeuft = false;

  const sicherstellen = async () => {
    if (laeuft) return;
    laeuft = true;
    try {
      const korb = await (await fetch('/cart.js')).json();
      if (korb.item_count > 0 && korb.attributes?.[ATTRIBUT] !== WERT) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [ATTRIBUT]: WERT } }),
        });
      }
    } catch (_) {
      /* Messung darf nie den Shop stoeren — naechster Ausloeser versucht es erneut */
    } finally {
      laeuft = false;
    }
  };

  document.addEventListener('shopify:cart:lines-update', () => {
    setTimeout(sicherstellen, 150);
  });

  const beimStart = () => {
    if (document.cookie.includes('cart=')) sicherstellen();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', beimStart, { once: true });
  } else {
    beimStart();
  }
})();

/* ==========================================================================
   PRESCH — Notpflaster 2: Warenkorb-Sections ohne Uebersetzungen nachholen
   ==========================================================================
   Befund (2026-09-02, gemessen in der Horizon-Zelle des Shopify-Rollouts):
   Die Antworten der Warenkorb-API (/cart/add, /cart/change, /cart/update mit
   `sections=...`) enthalten die nachgeladene Drawer-Section OHNE jede
   Uebersetzung — jeder `| t` steht als „Translation missing: de.…" im HTML,
   in 100 % der Faelle, auch beim zweiten und dritten Aufruf. Dieselbe Section
   per GET (Section Rendering API, `?sections=` oder `?section_id=`) kommt
   vollstaendig. Vorschau und Kontroll-Zelle (Shopify-Klon) sind sauber.
   Es ist ein Fehler im Rollout-Renderpfad von Shopify, nicht im Theme.

   Behelf: window.fetch wird fuer genau diese Endpunkte umhuellt. Traegt eine
   Antwort Sections mit dem Fehlertext, werden diese Sections per GET von der
   `sections_url` des Requests (Fallback: aktuelle Seite) nachgeholt und im
   JSON ersetzt, bevor Horizons Komponenten es verarbeiten. Alles andere geht
   unveraendert durch; bei jedem Fehler im Pflaster wird die Originalantwort
   zurueckgegeben. Der GET selbst faellt nicht unter das Muster — keine
   Schleife. Ausnahme von der Regel „kein window.fetch-Patch" oben: bewusst,
   weil der Kaufweg sonst kaputt aussieht; Vendor-Komponenten bleiben unberuehrt.

   RUECKBAU: Block entfernen, sobald der Rollout beendet ist oder Shopify den
   Fehler behoben hat. Kontrolle mit probe/drawertest2.js (Diagnose-Session).
   ========================================================================== */
(() => {
  'use strict';
  if (typeof window.fetch !== 'function' || window.__preschCartSectionsPatched) return;
  window.__preschCartSectionsPatched = true;

  const CART_API = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const KAPUTT = /Translation missing/;
  const original = window.fetch;

  const requestUrl = (input) => (typeof input === 'string' ? input : (input && input.url) || '');

  const sectionsUrlAus = (init) => {
    try {
      const body = init && init.body;
      if (body instanceof FormData) return body.get('sections_url') || '';
      if (typeof body === 'string') {
        const j = JSON.parse(body);
        return (j && j.sections_url) || '';
      }
    } catch (e) {}
    return '';
  };

  window.fetch = async function (input, init) {
    const antwort = await original.apply(this, arguments);
    try {
      if (!CART_API.test(requestUrl(input))) return antwort;
      const text = await antwort.clone().text();
      const daten = JSON.parse(text);
      if (!daten || typeof daten.sections !== 'object' || !daten.sections) return antwort;
      const kaputt = Object.keys(daten.sections).filter((k) => KAPUTT.test(daten.sections[k] || ''));
      if (!kaputt.length) return antwort;

      const basis = sectionsUrlAus(init) || window.location.pathname;
      const trenner = basis.includes('?') ? '&' : '?';
      // Kein Accept: application/json — auf Produkt-URLs antwortet Shopify dann
      // mit dem Produkt-JSON statt mit den Sections (gemessen).
      const frisch = await original(`${basis}${trenner}sections=${encodeURIComponent(kaputt.join(','))}`, {
        credentials: 'same-origin',
      });
      if (!frisch.ok) return antwort;
      const frischJson = await frisch.json();
      let ersetzt = 0;
      for (const k of kaputt) {
        if (frischJson[k] && !KAPUTT.test(frischJson[k])) {
          daten.sections[k] = frischJson[k];
          ersetzt++;
        }
      }
      if (!ersetzt) return antwort;
      return new Response(JSON.stringify(daten), {
        status: antwort.status,
        statusText: antwort.statusText,
        headers: antwort.headers,
      });
    } catch (e) {
      return antwort;
    }
  };
})();

/* =========================================================================
   Versandfrei-Angebot im Warenkorb: hinzufuegen, ohne die Schublade zu verlassen

   Das Angebot in `snippets/custom.cart-freeshipping.liquid` ist ein
   gewoehnliches Formular. Ohne diesen Abfangjaeger schickt der Browser es ab
   und landet auf /cart — der Kunde wird also aus der Schublade geworfen,
   obwohl er nur einen Artikel dazulegen wollte.

   Warum ein Listener auf `document` und kein Skript im Markup: Die Schublade
   wird bei jeder Warenkorbaenderung neu gemorpht. Ein <script> darin liefe
   danach nie wieder (dieselbe Falle, die im Kopf von custom.cart-upsell
   beschrieben ist). Ein delegierter Listener haengt am Dokument und ueberlebt
   jeden Morph.

   Der Weg zum Warenkorb ist derselbe wie beim Baustein „Passt dazu":
   Shopifys eigene Aktion, wenn es sie gibt, sonst /cart/add.js mit Neuladen.
   Faellt beides aus, laeuft das Formular ganz normal weiter — schlechter als
   ein sanftes Update, aber besser als ein Knopf, der nichts tut.
   ========================================================================= */
(() => {
  const HAKEN = '[data-presch-track="versandfrei-angebot"]';

  document.addEventListener('submit', async (ereignis) => {
    const formular = ereignis.target;
    if (!(formular instanceof HTMLFormElement)) return;
    const knopf = formular.querySelector(HAKEN);
    if (!knopf) return;

    const variante = Number(formular.querySelector('input[name="id"]')?.value);
    if (!variante) return;

    const update = window.Shopify?.actions?.updateCart;
    if (typeof update !== 'function') {
      // Ohne Shopifys Aktion kein sanftes Update: Formular normal abschicken.
      return;
    }

    // Positions-Eigenschaften aus dem Formular (properties[_Quelle] …) als
    // attributes mitgeben — sonst ginge die Herkunfts-Marke nur im
    // Formular-Fallback mit, nicht auf dem sanften Weg.
    const attributes = [...formular.querySelectorAll('input[name^="properties["]')].map((feld) => ({
      key: feld.name.slice('properties['.length, -1),
      value: feld.value,
    }));

    // GA4-Ereignis ueber das PRESCH-Analytics-Skript (presch-analytics.js).
    // Dort haengt die Consent-Warteschlange; ohne gtag verfaellt es still.
    // Der Mess-Haken data-presch-track war bisher nur eine Beschriftung fuer
    // einen Tag-Manager-Trigger, den es auf der Seite nicht gibt.
    if (typeof window.preschTrack === 'function') {
      window.preschTrack('versandfrei_angebot_click', {
        produkt_handle: knopf.dataset.trackProdukt || '',
        variante_id: knopf.dataset.trackVarianteId || '',
        preis_eur: Number(knopf.dataset.trackPreis || 0) / 100,
      });
    }

    ereignis.preventDefault();
    knopf.disabled = true;
    try {
      await update({ action: 'add', lines: [{ merchandiseId: variante, quantity: 1, attributes }] });
    } catch (e) {
      // Der Knopf wird wieder nutzbar, statt still kaputt dazustehen.
      knopf.disabled = false;
    }
  });
})();

/* =========================================================================
   Express-Checkout in der Schublade: auf dem Desktop aufgeklappt

   Der Aufklapper (<details class="custom-express"> in cart-summary.liquid)
   ist im Markup zu, weil das Handy den Platz braucht. Am Desktop hat die
   Schublade Platz, und dort sollen die Wallets offen stehen (Wunsch 20.09.).

   Ein <details> kennt keinen Zustand per CSS, deshalb diese paar Zeilen:
   Ab 750 px (Horizons Desktop-Schwelle) wird `open` gesetzt. Ein
   MutationObserver statt eines einmaligen Aufrufs, weil die Schublade bei
   jeder Warenkorbaenderung neu gemorpht wird und der Aufklapper dann wieder
   frisch (zu) im Dokument steht. Der Beobachter haengt am Dokument und
   ueberlebt jeden Morph — dieselbe Ueberlegung wie beim Angebots-Knopf oben.

   Nur oeffnen, nie schliessen: Wer am Desktop bewusst zuklappt, bleibt bis
   zum naechsten Morph zu. Auf dem Handy fasst das Skript nichts an.
   ========================================================================= */
(() => {
  const desktop = window.matchMedia('(min-width: 750px)');
  const oeffnen = () => {
    if (!desktop.matches) return;
    document.querySelectorAll('details.custom-express:not([open])').forEach((d) => {
      d.open = true;
    });
  };
  const beobachter = new MutationObserver(oeffnen);
  const start = () => {
    oeffnen();
    beobachter.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
  desktop.addEventListener('change', oeffnen);
})();

/* =========================================================================
   Sparset-Kopf im Warenkorb (snippets/custom.cart-set-kopf.liquid)

   Menge und Entfernen gelten für das ganze Set: alle Positionen mit
   `_Sparset: <id>` (Menge) bzw. auch die Zusatzprodukte (Entfernen) in EINEM
   Shopify.actions.updateCart-Aufruf — Horizon aktualisiert Schublade, Zähler
   und Summen dann selbst. Delegiert auf document, weil die Schublade bei
   jeder Änderung neu gemorpht wird (dieselbe Falle wie beim Angebots-Knopf).

   Aufklappen: je Set in sessionStorage gemerkt; ein MutationObserver setzt
   data-offen nach jedem Morph wieder, sonst klappte jede Mengenänderung zu.
   ========================================================================= */
(() => {
  const SPEICHER = 'presch_sparset_offen';
  const offen = () => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(SPEICHER) || '[]'));
    } catch {
      return new Set();
    }
  };
  const merken = (menge) => {
    try {
      sessionStorage.setItem(SPEICHER, JSON.stringify([...menge]));
    } catch {
      /* ohne Speicher bleibt es bis zum nächsten Morph offen */
    }
  };
  const anwenden = () => {
    const ids = offen();
    document.querySelectorAll('[data-sparset-auf]').forEach((knopf) => {
      const id = knopf.dataset.sparsetAuf;
      const auf = ids.has(id);
      if (knopf.getAttribute('aria-expanded') !== String(auf)) knopf.setAttribute('aria-expanded', String(auf));
      document.querySelectorAll(`[data-sparset-glied="${CSS.escape(id)}"]`).forEach((zeile) => {
        if (auf !== zeile.hasAttribute('data-offen')) zeile.toggleAttribute('data-offen', auf);
      });
    });
  };

  const aendern = async (knopf, lines) => {
    const update = window.Shopify?.actions?.updateCart;
    if (typeof update !== 'function' || !lines.length) return;
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
    try {
      await update({ action: lines.every((l) => l.quantity === 0) ? 'remove' : 'update', lines });
    } catch {
      knopf.disabled = false;
    }
    knopf.removeAttribute('aria-busy');
  };

  document.addEventListener('click', (e) => {
    const ziel = e.target instanceof Element ? e.target : null;
    if (!ziel) return;
    const auf = ziel.closest('[data-sparset-auf]');
    if (auf) {
      const ids = offen();
      const id = auf.dataset.sparsetAuf;
      ids.has(id) ? ids.delete(id) : ids.add(id);
      merken(ids);
      anwenden();
      return;
    }
    const menge = ziel.closest('[data-sparset-menge]');
    if (menge) {
      const neu = Number(menge.dataset.menge) + Number(menge.dataset.sparsetMenge);
      if (neu < 1) return;
      const keys = (menge.dataset.keys || '').split(',').filter(Boolean);
      aendern(menge, keys.map((id) => ({ id, quantity: neu })));
      return;
    }
    const weg = ziel.closest('[data-sparset-entfernen]');
    if (weg) {
      const keys = (weg.dataset.keys || '').split(',').filter(Boolean);
      aendern(weg, keys.map((id) => ({ id, quantity: 0 })));
    }
  });

  new MutationObserver(anwenden).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', anwenden, { once: true });
  } else {
    anwenden();
  }
})();
